import Icon from "@/components/Icon";
import Tag from "@/components/Tag";
import { getExperienceIndexCompetencyIcon } from "./experienceIndexIcons";
import {
  EXPERIENCE_INDEX_SCORE_MAX,
  formatExperienceIndexHubScore,
  type CompetencyStandingRow
} from "./experienceIndexResults";

const PRIORITY_TOOLTIP =
  "Priority for the selected comparison role — one of the six competencies this role values most on its rubric.";

function scorePercent(value: number) {
  return Math.max(0, Math.min(100, (value / EXPERIENCE_INDEX_SCORE_MAX) * 100));
}

function standingAccessibilityLabel(row: CompetencyStandingRow) {
  const priorityLabel = row.isPriority ? ", priority" : "";

  if (!row.isComplete) {
    return `${row.label}${priorityLabel}: not yet answered.`;
  }

  const scoreLabel = formatExperienceIndexHubScore(row.memberScore ?? 0);

  if (row.desiredScore == null) {
    return `${row.label}${priorityLabel}: ${scoreLabel}.`;
  }

  return `${row.label}${priorityLabel}: ${scoreLabel}, role rubric ${row.desiredScore} out of ${EXPERIENCE_INDEX_SCORE_MAX}.`;
}

export default function ExperienceIndexCompetencyStandingRow({
  row,
  onViewAnswers
}: {
  row: CompetencyStandingRow;
  onViewAnswers?: (competencyId: string) => void;
}) {
  const accessibilityLabel = standingAccessibilityLabel(row);
  const isDrillDown = Boolean(onViewAnswers);
  const content = (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-extended-pgasilver-brand-color">
        <Icon
          name={getExperienceIndexCompetencyIcon(row.label)}
          size={20}
          className="text-extended-pgasilver-brand-on-color"
        />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-title-small text-sys-brand-on-surface">{row.label}</p>
        {row.isComplete ? (
          <div className="relative mt-1 h-4">
            <div
              className="h-1.5 overflow-hidden rounded-full bg-sys-brand-surface-container-highest"
              data-testid={`career-plan-competency-score-bar-${row.id}`}
            >
              <div
                className="h-1.5 rounded-full bg-extended-success-brand-color"
                style={{ width: `${scorePercent(row.memberScore ?? 0)}%` }}
              />
            </div>
            {row.desiredScore != null ? (
              <div
                className="absolute top-0 h-4 w-0.5 rounded-full bg-sys-brand-on-surface"
                style={{ left: `${scorePercent(row.desiredScore)}%`, transform: "translateX(-50%)" }}
                data-testid={`career-plan-radar-benchmark-${row.id}`}
              />
            ) : null}
          </div>
        ) : (
          <div className="mt-1">
            <Tag tagType="surface" size="small">
              Not yet answered
            </Tag>
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {row.isComplete ? (
          <p className="text-body-small text-sys-brand-on-surface-variant">
            {formatExperienceIndexHubScore(row.memberScore ?? 0)}
          </p>
        ) : null}
        {row.isPriority ? (
          <span title={PRIORITY_TOOLTIP} aria-hidden="true" data-testid="career-plan-competency-priority">
            <Icon name="my_location" size={16} className="text-sys-brand-primary" />
          </span>
        ) : null}
        {isDrillDown ? <Icon name="chevron_right" size={20} className="text-sys-brand-on-surface-variant" /> : null}
      </div>
    </div>
  );
  const className = `rounded-lg border border-sys-brand-outline-variant bg-sys-brand-surface p-3${
    isDrillDown ? " w-full text-left transition-colors hover:bg-sys-brand-surface-container-low" : ""
  }`;

  if (onViewAnswers) {
    return (
      <button
        type="button"
        className={className}
        data-testid={`career-plan-competency-standing-${row.id}`}
        aria-label={`${accessibilityLabel} View answers.`}
        onClick={() => onViewAnswers(row.id)}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      role="group"
      className={className}
      data-testid={`career-plan-competency-standing-${row.id}`}
      aria-label={accessibilityLabel}
    >
      {content}
    </div>
  );
}
